import { ApolloClientOptions, ApolloLink, createHttpLink, from, NormalizedCacheObject } from '@apollo/client';
import { useMemo } from 'react';
import AppFeatureDirectiveLink from '@tools/AppFeatureDirectiveLink';
import { setContext } from '@apollo/client/link/context';
import getConfig from 'next/config';
import { useCsrfTokenLink } from '@/apollo/csrfTokenLink';
import { USER_AGENT_HEADER } from '@tools/serverSideProps';
import { createCache } from './cache';
import { linkFetchAndRetryIfServerBusy, restLink, speedNetworkingLink, videoDiscussionLink } from './links';
import { onErrorLink } from './onErrorLink';
import { useAuthExpired } from './authExpired';
import { createGraphLink } from './createGraphLink';

const { serverRuntimeConfig } = getConfig();

export function useApolloClientOptions({
  appFeatures,
  eventId,
  attendeeId
}): ApolloClientOptions<NormalizedCacheObject> {
  const cache = useMemo(() => {
    return createCache();
  }, []);

  const appFeatureDirectiveLink = useMemo(() => {
    return new AppFeatureDirectiveLink(appFeatures);
  }, [appFeatures]);

  const { authExpired } = useAuthExpired();
  const terminatingLink = useMemo(() => {
    return createGraphLink(eventId, attendeeId, authExpired);
  }, [eventId, attendeeId, authExpired]);
  const csrfTokenLink = useCsrfTokenLink(attendeeId);

  return useMemo(() => {
    return {
      cache,
      link: from([
        csrfTokenLink,
        restLink,
        appFeatureDirectiveLink,
        onErrorLink,
        ApolloLink.split(operation => operation.getContext().clientName === 'speed-networking', speedNetworkingLink),
        ApolloLink.split(operation => operation.getContext().clientName === 'video-discussions', videoDiscussionLink),
        terminatingLink
      ])
    };
  }, [cache, appFeatureDirectiveLink, terminatingLink]);
}

export const getServerSideApolloClientOptions = (
  graphUri: string,
  environment: string
): ApolloClientOptions<NormalizedCacheObject> => {
  if (typeof window !== 'undefined') {
    throw new Error('Do not use getServerSideApolloClientOptions on the client');
  }

  const cache = createCache();

  const link = createHttpLink({
    uri: graphUri,
    fetch: linkFetchAndRetryIfServerBusy
  });

  const authLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        [USER_AGENT_HEADER]: `attendee-experience-web (${environment})`,
        authorization: `API_KEY ${serverRuntimeConfig.API_KEY}`,
        ...(environment ? { environment } : {})
      }
    };
  });

  return {
    link: authLink.concat(link),
    cache
  };
};
